import { createBasicExample } from "./examples/basic";
import { createCodesSummaryExample } from "./examples/codes-summary";
import { createFormTemplateExample } from "./examples/form-template";
import { createLabelSheetExample } from "./examples/label-sheet";
import { createPaginationExample } from "./examples/pagination";
import type { PreviewOptions } from "../src/index";

type DemoPreviewDefaults = Omit<PreviewOptions, "container">;

interface DemoExample {
  id: string;
  title: string;
  description: string;
  job: {
    preview(options: PreviewOptions): Promise<HTMLIFrameElement>;
    print(): Promise<void>;
  };
  defaultPreviewOptions: DemoPreviewDefaults;
}

function buildExamples(repeatHeaderOnPageBreak: boolean): DemoExample[] {
  return [
    createBasicExample({ repeatHeaderOnPageBreak }),
    createPaginationExample(),
    createCodesSummaryExample(),
    createLabelSheetExample(),
    createFormTemplateExample()
  ];
}

// Drive the demo shell: scenario switching, preview refresh, and print actions
// all route through the same PrintJob-based examples.
async function main() {
  const previewRoot = document.querySelector<HTMLElement>("#preview");
  const printButton = document.querySelector<HTMLButtonElement>("#print");
  const previewButton = document.querySelector<HTMLButtonElement>("#refresh");
  const exampleSelectNode = document.querySelector<HTMLSelectElement>("#example-select");
  const repeatHeaderToggleNode = document.querySelector<HTMLInputElement>("#repeat-header-toggle");
  const pagedPreviewToggleNode = document.querySelector<HTMLInputElement>("#paged-preview-toggle");
  const pageNumbersToggleNode = document.querySelector<HTMLInputElement>("#page-numbers-toggle");
  const exampleTitleNode = document.querySelector<HTMLElement>("#example-title");
  const exampleDescriptionNode = document.querySelector<HTMLElement>("#example-description");

  if (
    !previewRoot ||
    !printButton ||
    !previewButton ||
    !exampleSelectNode ||
    !repeatHeaderToggleNode ||
    !pagedPreviewToggleNode ||
    !pageNumbersToggleNode ||
    !exampleTitleNode ||
    !exampleDescriptionNode
  ) {
    throw new Error("Demo root elements are missing.");
  }

  const previewContainer: HTMLElement = previewRoot;
  const exampleSelect: HTMLSelectElement = exampleSelectNode;
  const repeatHeaderToggle: HTMLInputElement = repeatHeaderToggleNode;
  const pagedPreviewToggle: HTMLInputElement = pagedPreviewToggleNode;
  const pageNumbersToggle: HTMLInputElement = pageNumbersToggleNode;
  const exampleTitle: HTMLElement = exampleTitleNode;
  const exampleDescription: HTMLElement = exampleDescriptionNode;

  let currentExamples = buildExamples(repeatHeaderToggle.checked);
  let currentExample: DemoExample = currentExamples[0] ?? (() => {
    throw new Error("No demo examples are available.");
  })();

  // Sync the select menu with the currently available examples.
  function populateExampleSelect() {
    exampleSelect.innerHTML = currentExamples
      .map((example) => `<option value="${example.id}">${example.title}</option>`)
      .join("");
  }

  function syncExampleMeta(example: DemoExample) {
    exampleTitle.textContent = example.title;
    exampleDescription.textContent = example.description;
  }

  function resolvePreviewOptions(example: DemoExample): PreviewOptions {
    const previewOptions: PreviewOptions = {
      ...example.defaultPreviewOptions,
      container: previewContainer
    };

    // The pagination example owns paged preview by default, but the global
    // toggle allows users to experiment with the same job in document mode.
    if (pagedPreviewToggle.checked) {
      previewOptions.mode = "paged";
      previewOptions.showPageGap = true;
      previewOptions.showAutoPaginationHints = true;
    } else {
      previewOptions.mode = "document";
    }

    previewOptions.showPageNumbers = pageNumbersToggle.checked;
    return previewOptions;
  }

  // Preview rendering is intentionally centralized so every UI control reuses
  // the same code path and therefore exercises the same library behavior.
  async function renderCurrentExample() {
    syncExampleMeta(currentExample);
    await currentExample.job.preview(resolvePreviewOptions(currentExample));
  }

  function rebuildExamples() {
    const activeId = currentExample.id;
    currentExamples = buildExamples(repeatHeaderToggle.checked);
    populateExampleSelect();
    currentExample = currentExamples.find((example) => example.id === activeId) ?? currentExamples[0] ?? currentExample;
    exampleSelect.value = currentExample.id;
  }

  populateExampleSelect();
  syncExampleMeta(currentExample);
  exampleSelect.value = currentExample.id;

  previewButton.addEventListener("click", () => {
    void renderCurrentExample();
  });

  printButton.addEventListener("click", () => {
    void currentExample.job.print();
  });

  exampleSelect.addEventListener("change", () => {
    currentExample = currentExamples.find((example) => example.id === exampleSelect.value) ?? currentExample;
    void renderCurrentExample();
  });

  repeatHeaderToggle.addEventListener("change", () => {
    rebuildExamples();
    void renderCurrentExample();
  });

  pagedPreviewToggle.addEventListener("change", () => {
    void renderCurrentExample();
  });

  pageNumbersToggle.addEventListener("change", () => {
    void renderCurrentExample();
  });

  await renderCurrentExample();
}

void main();
